import React from 'react'

import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'

import BrandService from 'src/services/BrandService'
import ProductService from 'src/services/ProductService'
import UploadService from 'src/services/UploadService'
import { route } from 'src/utils/routes'
import { IProduct } from 'src/services/types/product'

import SearchSelect from 'src/components/Form/SearchSelect'
import ImageUploader from 'src/components/Image/ImageUploader'

const brandService = new BrandService()
const productService = new ProductService()
const uploadService = new UploadService()

interface IProps extends RouteComponentProps {
  user: any
  type?: string
  product?: IProduct
  updateProduct?: (updatedValues: IProduct) => void
  history: any
}

const ProductForm: React.FC<IProps> = ({
  user,
  type,
  product,
  updateProduct,
  history,
}) => {
  const { addToast } = useToasts()
  const _isMounted = React.useRef(false)

  const [brands, setBrands] = React.useState<any[]>([])
  const [isUploading, setIsUploading] = React.useState<boolean>(false)

  React.useEffect(() => {
    _isMounted.current = true

    brandService.fetchByManufacturer(user.manufacturer_id).then((brands) => {
      const activeBrands = brands
        .filter((brand) => brand.status === 'active')
        .map((brand) => ({
          value: brand._id,
          label: brand.name,
        }))
      _isMounted.current && setBrands(activeBrands)
    })

    return () => {
      _isMounted.current = false
    }
  }, [user.manufacturer_id])

  const formConfig = {
    initialValues: {
      brand: product
        ? {
            value: product.brand._id,
            label: product.brand.name,
          }
        : null,
      name: product ? product.name : '',
      status: product ? product.status : 'active',
      sku_id: product ? product.sku_id : '',
      case_count: product ? product.case_count : '',
      price: product ? product.price : '',
      price_rrp: product ? product.price_rrp : '',
      size: product ? product.size : '0',
      weight: product ? product.weight : '0',
    },
    validationSchema: Yup.object({
      brand: Yup.string().required('Brand field is required.').nullable(),
      name: Yup.string()
        .required('Name field is required.')
        .min(3, 'Name must be at least 3 characters.'),
      status: Yup.string().required('Status field is required.'),
      sku_id: Yup.string().required('SKU ID field is required.'),
      case_count: Yup.number().required('Case count field is required.'),
      price: Yup.number().required('Price field is required.'),
      price_rrp: Yup.number().required(
        'Recommended retail price field is required.'
      ),
      size: Yup.number().required('Size field is required.'),
      weight: Yup.number().required('Weight field is required.'),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      const finalValues: any = {
        name: values.name,
        status: values.status,
        sku_id: values.sku_id,
        case_count: values.case_count,
        price: values.price,
        price_rrp: values.price_rrp,
        size: values.size,
        weight: values.weight,
        brand_id: values.brand.value,
      }

      if (type === 'update') {
        finalValues._id = product._id
        finalValues.upload_id = product.upload_id
      }

      productService[type](finalValues)
        .then((productId) => {
          setSubmitting(false)
          // resetForm()

          if (type === 'update') {
            addToast('Product successfully updated.', { appearance: 'success' })
            updateProduct(finalValues)
          } else {
            addToast('Product successfully created.', { appearance: 'success' })
            history.push(route('product_update', { id: productId }))
          }
        })
        .catch((error) => {
          setSubmitting(false)
          addToast(error.message, { appearance: 'error' })
        })
    },
  }

  const doUpload = (uploadData): Promise<any> => {
    return new Promise((resolve) => {
      uploadService
        .upload(uploadData, `product_${product._id}`)
        .then((uploadId) => {
          const updatedProduct = {
            _id: product._id,
            name: product.name,
            status: product.status,
            sku_id: product.sku_id,
            case_count: product.case_count,
            price: product.price,
            price_rrp: product.price_rrp,
            size: product.size,
            weight: product.weight,
            brand_id: product.brand_id,
            upload_id: uploadId,
          }

          productService
            .update(updatedProduct)
            .then(() => {
              setIsUploading(false)

              updateProduct(updatedProduct)
              addToast('Image upload successful.', {
                appearance: 'success',
              })
              updateProduct(updatedProduct)
              resolve()
            })
            .catch((error) => {
              setIsUploading(false)
              addToast(error.message, { appearance: 'error' })
              resolve()
            })
        })
        .catch((error) => {
          setIsUploading(false)
          addToast(error.message, { appearance: 'error' })
          resolve()
        })
    })
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formConfig.initialValues}
      validationSchema={formConfig.validationSchema}
      onSubmit={formConfig.onSubmit}
    >
      {({ errors, touched, values, handleSubmit, ...formik }) => (
        <Form onSubmit={handleSubmit}>
          {/* Product status */}
          <Form.Group as={Row} className="align-items-center">
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Status *</Form.Label>
            </Col>
            <Col md={6}>
              <div className="d-flex">
                <Field
                  as={Form.Check}
                  type="radio"
                  id="active"
                  name="status"
                  value="active"
                  label="Active"
                  checked={values.status === 'active'}
                  custom
                />

                <Field
                  as={Form.Check}
                  type="radio"
                  id="inactive"
                  name="status"
                  value="inactive"
                  label="Inactive"
                  checked={values.status === 'inactive'}
                  className="ml-3"
                  custom
                />
              </div>
            </Col>
          </Form.Group>
          <hr />

          {type === 'update' && product ? (
            <>
              {/* Image */}
              <Form.Group as={Row}>
                <Col md={3} className="mb-2 mb-md-0">
                  <Form.Label>Image</Form.Label>
                  <Form.Text className="text-muted">Product image</Form.Text>
                </Col>
                <Col md={6}>
                  <ImageUploader
                    uploadPath={product.upload ? product.upload.path : null}
                    doUpload={doUpload}
                  />
                </Col>
              </Form.Group>
              <hr />
            </>
          ) : null}

          {/* Brand */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Brand *</Form.Label>
              <Form.Text className="text-muted">{`Product's brand`}</Form.Text>
            </Col>
            <Col md={6}>
              <SearchSelect
                name="brand"
                options={brands}
                value={values.brand}
                placeholder="Select brand"
                onBlur={() => {
                  formik.setFieldTouched('brand', true)
                }}
                onChange={(selected) => {
                  formik.setFieldValue('brand', selected)
                }}
                isInvalid={touched.brand && errors.brand ? true : false}
                isDisabled={!brands.length}
              />
              <ErrorMessage
                name="brand"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product name */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Name *</Form.Label>
              <Form.Text className="text-muted">Name of product</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="name"
                type="text"
                isInvalid={touched.name && errors.name ? true : false}
              />
              <ErrorMessage
                name="name"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* SKU ID */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>SKU ID *</Form.Label>
              <Form.Text className="text-muted">Product SKU ID</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="sku_id"
                type="text"
                isInvalid={touched.sku_id && errors.sku_id ? true : false}
              />
              <ErrorMessage
                name="sku_id"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product case count */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Case Count *</Form.Label>
              <Form.Text className="text-muted">Product case count</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="case_count"
                type="number"
                isInvalid={
                  touched.case_count && errors.case_count ? true : false
                }
              />
              <ErrorMessage
                name="case_count"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product price */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Price *</Form.Label>
              <Form.Text className="text-muted">Product price.</Form.Text>
            </Col>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Prepend>
                  <InputGroup.Text id="currency">₦</InputGroup.Text>
                </InputGroup.Prepend>
                <Field
                  as={Form.Control}
                  name="price"
                  type="number"
                  isInvalid={touched.price && errors.price ? true : false}
                />
              </InputGroup>
              <ErrorMessage
                name="price"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product price (RRP) */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Price (RRP) *</Form.Label>
              <Form.Text className="text-muted">
                Recommended retail price.
              </Form.Text>
            </Col>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Prepend>
                  <InputGroup.Text id="currency">₦</InputGroup.Text>
                </InputGroup.Prepend>
                <Field
                  as={Form.Control}
                  name="price_rrp"
                  type="number"
                  isInvalid={
                    touched.price_rrp && errors.price_rrp ? true : false
                  }
                />
              </InputGroup>
              <ErrorMessage
                name="price_rrp"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product size */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Size *</Form.Label>
              <Form.Text className="text-muted">Product size.</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="size"
                type="number"
                isInvalid={touched.size && errors.size ? true : false}
              />
              <ErrorMessage
                name="size"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          {/* Product weight */}
          <Form.Group as={Row}>
            <Col md={3} className="mb-2 mb-md-0">
              <Form.Label>Weight *</Form.Label>
              <Form.Text className="text-muted">Product weight.</Form.Text>
            </Col>
            <Col md={6}>
              <Field
                as={Form.Control}
                name="weight"
                type="number"
                isInvalid={touched.weight && errors.weight ? true : false}
              />
              <ErrorMessage
                name="weight"
                component="span"
                className="invalid-feedback"
              />
            </Col>
          </Form.Group>
          <hr />

          <Button
            type="submit"
            variant="success"
            className="mt-4"
            disabled={
              formik.isSubmitting ||
              !formik.dirty ||
              !formik.isValid ||
              isUploading
            }
          >
            {formik.isSubmitting ? (
              <figure className="spinner button white" />
            ) : type === 'create' ? (
              'Create Product'
            ) : (
              'Update Product'
            )}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

ProductForm.defaultProps = {
  type: 'create',
}

export default withRouter(ProductForm)
